import React, { useState, ChangeEvent } from 'react';
import {
  SimpleShowLayout,
  Record,
  useTranslate,
  useNotify,
  useRedirect,
  useMutation,
} from 'react-admin';
import { Button, CircularProgress, TextField } from '@material-ui/core';
import SectionTitle from '../SectionTitle';
import CustomSelect from '../CustomSelect';
// import CustomCheckbox from '../CustomCheckbox';
import messages from '../DoctorReportForm/messages';
import ConfirmationModal from '../DoctorReportForm/ConfirmationModal';

import './styles.scss';

interface HealthRiskCheckConfiguratorProps {
  record: Record | undefined;
  readOnly?: boolean;
  currentResource?: string;
}

const getDoctorReport = (record: Record | undefined) => {
  if (!record) {
    return null;
  }

  const { services } = record.results;

  const doctorReport = services.find(
    (service: any) => service.service === 'doctorsReport',
  );

  return doctorReport;
};

const getBloodSugarOrBloodLipidsValue = (value: string | undefined | null) => {
  if (!value) {
    return '';
  }

  if (value === 'detected') {
    return 'true';
  }

  return 'false';
};

const getDoctorRecommendedActionsValue = (
  recommendedActions: string[] | undefined | null,
  action: string,
) => {
  if (!recommendedActions) {
    return '';
  }

  if (recommendedActions.includes(action)) {
    return 'true';
  }

  return 'false';
};

const HealthRiskCheckConfigurator: React.FC<HealthRiskCheckConfiguratorProps> =
  ({ record, readOnly = false, currentResource }) => {
    const recordData = record?.test ? record?.test : record;
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [openModal, setOpenModal] = useState<boolean>(false);

    const doctorReport = getDoctorReport(recordData);
    // Configurations
    const [elevatedBloodSugar, setElevatedBloodSugar] = useState<string>(
      getBloodSugarOrBloodLipidsValue(
        recordData?.doctorDiagnosis?.elevatedBloodSugar,
      ),
    );
    const [elevatedBloodLipids, setElevatedBloodLipids] = useState<string>(
      getBloodSugarOrBloodLipidsValue(
        recordData?.doctorDiagnosis?.elevatedBloodLipids,
      ),
    );
    const [consultDoctor, setConsultDoctor] = useState<string>(
      getDoctorRecommendedActionsValue(
        recordData?.doctorRecommendedActions,
        'consultDoctor',
      ),
    );
    const [adjustDietWithNutritionAdvice, setAdjustDietWithNutritionAdvice] =
      useState<string>(
        getDoctorRecommendedActionsValue(
          recordData?.doctorRecommendedActions,
          'adjustDietWithNutritionAdvice',
        ),
      );
    const [retestInSixMonths, setRetestInSixMonths] = useState<string>(
      getDoctorRecommendedActionsValue(
        recordData?.doctorRecommendedActions,
        'retestInSixMonths',
      ),
    );
    const [retestInOneYear, setRetestInOneYear] = useState<string>(
      getDoctorRecommendedActionsValue(
        recordData?.doctorRecommendedActions,
        'retestInOneYear',
      ),
    );

    // Report form states
    const [doctorName, setDoctorName] = useState<string>('Dr. Robert Döring');

    const [report, setReport] = useState<string>(
      doctorReport ? doctorReport.doctorsReport : '',
    );

    // Add error states
    const [errors, setErrors] = useState({
      doctorName: false,
      report: false,
      elevatedBloodLipids: false,
      elevatedBloodSugar: false,
      consultDoctor: false,
      adjustDietWithNutritionAdvice: false,
      retestInSixMonths: false,
      retestInOneYear: false,
    });

    const createDoctorRecommendedActionsArray = () => {
      const actions: string[] = [];

      if (consultDoctor === 'true') {
        actions.push('consultDoctor');
      }

      if (adjustDietWithNutritionAdvice === 'true') {
        actions.push('adjustDietWithNutritionAdvice');
      }

      if (retestInSixMonths === 'true') {
        actions.push('retestInSixMonths');
      }

      if (retestInOneYear === 'true') {
        actions.push('retestInOneYear');
      }

      return actions;
    };

    const getDoctorDiagnosis = () => ({
      elevatedBloodLipids,
      elevatedBloodSugar,
    });

    const [addReport, { loading }] = useMutation(
      {
        type: 'update',
        resource: 'tests',
        payload: {
          id: recordData?.id,
          data: {
            report,
            doctorName,
            isPharmacyReport: false,
            pharmacyName: null,
            doctorRecommendedActions: createDoctorRecommendedActionsArray(),
            doctorDiagnosis: getDoctorDiagnosis(),
          },
        },
      },
      {
        onSuccess: () => {
          // Redirect to the current resource list, or fallback to regular healthRiskCheck
          const redirectPath = currentResource
            ? `/${currentResource}`
            : '/tests/healthRiskCheck';
          redirect(redirectPath);
          notify(messages.addReportSuccessMessage, { type: 'success' });
        },
        onFailure: () =>
          notify(messages.addReportErrorMessage, { type: 'warning' }),
      },
    );

    const validateFields = () => {
      const newErrors = {
        doctorName: !doctorName || doctorName.trim() === '',
        report: !report || report.trim() === '',
        elevatedBloodLipids: !elevatedBloodLipids,
        elevatedBloodSugar: !elevatedBloodSugar,
        consultDoctor: !consultDoctor,
        adjustDietWithNutritionAdvice: !adjustDietWithNutritionAdvice,
        retestInSixMonths: !retestInSixMonths,
        retestInOneYear: !retestInOneYear,
      };

      setErrors(newErrors);
      return !Object.values(newErrors).some((error) => error);
    };

    const handleFormSubmit = () => {
      const isValid = validateFields();

      if (!isValid) {
        setOpenModal(false);
        return;
      }

      if (isValid) {
        addReport();
      }
    };

    // Report form handlers
    const handleDoctorNameInputChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setDoctorName(event.target.value);
      setErrors((prev) => ({ ...prev, doctorName: false }));
    };

    const handleReportTextareaChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setReport(event.target.value);
      setErrors((prev) => ({ ...prev, report: false }));
    };

    const handleElevatedBloodLipidsChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setElevatedBloodLipids(event.target.value);
      setErrors((prev) => ({ ...prev, elevatedBloodLipids: false }));
    };

    const handleElevatedBloodSugarChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setElevatedBloodSugar(event.target.value);
      setErrors((prev) => ({ ...prev, elevatedBloodSugar: false }));
    };

    const handleConsultDoctorChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setConsultDoctor(event.target.value);
      setErrors((prev) => ({ ...prev, consultDoctor: false }));
    };

    const handleAdjustDietWithNutritionAdviceChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setAdjustDietWithNutritionAdvice(event.target.value);
      setErrors((prev) => ({ ...prev, adjustDietWithNutritionAdvice: false }));
    };

    const handleRetestInSixMonthsChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      const { value } = event.target;

      setRetestInSixMonths(value);
      setErrors((prev) => ({ ...prev, retestInSixMonths: false }));

      if (value === 'true') {
        setRetestInOneYear('false');
        setErrors((prev) => ({ ...prev, retestInOneYear: false }));
      }
    };

    const handleRetestInOneYearChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      const { value } = event.target;

      setRetestInOneYear(value);
      setErrors((prev) => ({ ...prev, retestInOneYear: false }));

      if (value === 'true') {
        setRetestInSixMonths('false');
        setErrors((prev) => ({ ...prev, retestInSixMonths: false }));
      }
    };

    const handleCloseModal = () => {
      setOpenModal(false);
    };

    const handleOpenModal = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setOpenModal(true);
    };

    return (
      <SimpleShowLayout className="healthRiskCheckConfigurator">
        <>
          <ConfirmationModal
            handleClose={handleCloseModal}
            open={openModal}
            confirmationFunction={handleFormSubmit}
            loading={loading || false}
            isFertilityCheck
          />
          <form onSubmit={handleOpenModal}>
            <div className="healthRiskCheckConfigurator__section">
              <SectionTitle
                className="healthRiskCheckConfigurator__title"
                title={translate('testResults.diagnosis')}
              />
              <div className="healthRiskCheckConfigurator__section">
                <div className="healthRiskCheckConfigurator__item--title">
                  {translate(
                    'healthRiskCheck.configurator.configureIdentifiedHealthRisks',
                  )}
                </div>
                <div className="healthRiskCheckConfigurator__item">
                  <div>
                    {translate(
                      'healthRiskCheck.configurator.elevatedBloodLipids',
                    )}
                  </div>
                  <CustomSelect
                    emptyLabel={translate(
                      'healthRiskCheck.configurator.nothingSelected',
                    )}
                    options={[
                      {
                        value: 'true',
                        label: translate(
                          'healthRiskCheck.configurator.detected',
                        ),
                      },
                      {
                        value: 'false',
                        label: translate(
                          'healthRiskCheck.configurator.notDetected',
                        ),
                      },
                    ]}
                    value={elevatedBloodLipids}
                    onChange={handleElevatedBloodLipidsChange}
                    error={errors.elevatedBloodLipids}
                    disabled={readOnly}
                  />
                </div>
                <div className="healthRiskCheckConfigurator__item">
                  <div>
                    {translate(
                      'healthRiskCheck.configurator.elevatedBloodSugar',
                    )}
                  </div>
                  <CustomSelect
                    emptyLabel={translate(
                      'healthRiskCheck.configurator.nothingSelected',
                    )}
                    options={[
                      {
                        value: 'true',
                        label: translate(
                          'healthRiskCheck.configurator.detected',
                        ),
                      },
                      {
                        value: 'false',
                        label: translate(
                          'healthRiskCheck.configurator.notDetected',
                        ),
                      },
                    ]}
                    value={elevatedBloodSugar}
                    onChange={handleElevatedBloodSugarChange}
                    error={errors.elevatedBloodSugar}
                    disabled={readOnly}
                  />
                </div>
                <div className="healthRiskCheckConfigurator__item--title">
                  {translate(
                    'healthRiskCheck.configurator.configureRecommendedActionsToBeShown',
                  )}
                </div>
                <div className="healthRiskCheckConfigurator__item">
                  <div>
                    {translate('healthRiskCheck.configurator.consultDoctor')}
                  </div>
                  <CustomSelect
                    emptyLabel={translate(
                      'healthRiskCheck.configurator.selectYesOrNo',
                    )}
                    options={[
                      {
                        value: 'true',
                        label: translate('healthRiskCheck.configurator.yes'),
                      },
                      {
                        value: 'false',
                        label: translate('healthRiskCheck.configurator.no'),
                      },
                    ]}
                    value={consultDoctor}
                    onChange={handleConsultDoctorChange}
                    error={errors.consultDoctor}
                    disabled={readOnly}
                  />
                </div>
                <div className="healthRiskCheckConfigurator__item">
                  <div>
                    {translate(
                      'healthRiskCheck.configurator.adjustDietWithNutritionAdvice',
                    )}
                  </div>
                  <CustomSelect
                    emptyLabel={translate(
                      'healthRiskCheck.configurator.selectYesOrNo',
                    )}
                    options={[
                      {
                        value: 'true',
                        label: translate('healthRiskCheck.configurator.yes'),
                      },
                      {
                        value: 'false',
                        label: translate('healthRiskCheck.configurator.no'),
                      },
                    ]}
                    value={adjustDietWithNutritionAdvice}
                    onChange={handleAdjustDietWithNutritionAdviceChange}
                    error={errors.adjustDietWithNutritionAdvice}
                    disabled={readOnly}
                  />
                </div>
                <div className="healthRiskCheckConfigurator__item">
                  <div>
                    {translate(
                      'healthRiskCheck.configurator.retestInSixMonths',
                    )}
                  </div>
                  <CustomSelect
                    emptyLabel={translate(
                      'healthRiskCheck.configurator.selectYesOrNo',
                    )}
                    options={[
                      {
                        value: 'true',
                        label: translate('healthRiskCheck.configurator.yes'),
                      },
                      {
                        value: 'false',
                        label: translate('healthRiskCheck.configurator.no'),
                      },
                    ]}
                    value={retestInSixMonths}
                    onChange={handleRetestInSixMonthsChange}
                    error={errors.retestInSixMonths}
                    disabled={readOnly}
                  />
                </div>
                <div className="healthRiskCheckConfigurator__item">
                  <div>
                    {translate('healthRiskCheck.configurator.retestInOneYear')}
                  </div>
                  <CustomSelect
                    emptyLabel={translate(
                      'healthRiskCheck.configurator.selectYesOrNo',
                    )}
                    options={[
                      {
                        value: 'true',
                        label: translate('healthRiskCheck.configurator.yes'),
                      },
                      {
                        value: 'false',
                        label: translate('healthRiskCheck.configurator.no'),
                      },
                    ]}
                    value={retestInOneYear}
                    onChange={handleRetestInOneYearChange}
                    error={errors.retestInOneYear}
                    disabled={readOnly}
                  />
                </div>
              </div>
            </div>
            <div className="healthRiskCheckConfigurator__section">
              <div className="form">
                <SectionTitle
                  className="healthRiskCheckConfigurator__title"
                  title={translate('testResults.doctorsReport')}
                />
                <TextField
                  className="input"
                  id="doctor-name"
                  label={translate(messages.inputLabel)}
                  value={doctorName}
                  variant="outlined"
                  onChange={handleDoctorNameInputChange}
                  error={errors.doctorName}
                  helperText={
                    errors.doctorName ? translate('ra.validation.required') : ''
                  }
                  disabled={readOnly}
                />
                <TextField
                  className="textarea"
                  id="report"
                  label={translate(messages.textareaLabel)}
                  multiline
                  value={report}
                  rows={8}
                  variant="outlined"
                  onChange={handleReportTextareaChange}
                  error={errors.report}
                  helperText={
                    errors.report ? translate('ra.validation.required') : ''
                  }
                  disabled={readOnly}
                />
                {!readOnly && (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="button"
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size="24.5px" />
                    ) : (
                      translate(messages.buttonText)
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </>
      </SimpleShowLayout>
    );
  };

export default HealthRiskCheckConfigurator;
